"use client";
import ColorNextSteps from "@/components/ColorNextSteps";

import React, { Suspense, useState, useRef, useEffect } from "react";
import {
  Container,
  Title,
  Text,
  Paper,
  Group,
  Button,
  Stack,
  SimpleGrid,
  Image as MantineImage,
  Modal,
  Box,
  LoadingOverlay,
  ActionIcon,
  Flex,
  Divider,
  Badge,
  Slider,
  Alert,
} from "@mantine/core";
import {
  IconBrain,
  IconCamera,
  IconDownload,
  IconRefresh,
  IconRobot,
  IconUpload,
  IconArrowRight,
  IconUsers,
  IconX,
  IconCheck,
} from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import { useDisclosure, useMediaQuery } from "@mantine/hooks";
import { colorSeasons } from "@/constants";
import JSZip from "jszip";
import Link from "next/link";
import { useRouter, useSearchParams, useParams } from "next/navigation";
import { useTranslations } from "next-intl";
import { trackCtaClick, trackCheckoutStart, trackGizmo } from "@/lib/analytics";
import { FREE_DRAPES_V1 } from "@/lib/freeDrapesFlag";
import { QUICK_ANALYSIS_V1 } from "@/lib/quickAnalysisFlag";
import { getPriceBucket } from "@/lib/priceExperiment";
import { drawDrapeOverlay } from "@/lib/drapeOverlay";
import { createColorAnalysisCheckout } from "@/lib/colorAnalysisCheckout";
import { getStoredLeadEmail } from "@/components/CheckoutEmailStep";

// Framing step geometry. The oval used everywhere is 1 : 1.25 (rx : ry), so the
// framing preview box uses that same ratio and the oval fills it.
const FRAME_W = 300;
const FRAME_H = 375; // 300 * 1.25
const FRAME_QUALITY = 3; // baked canvas is FRAME_W*Q x FRAME_H*Q for crisp output
const MIN_FRAME_SCALE = 0.5; // below 1 the gray backdrop shows inside the oval
const MAX_FRAME_SCALE = 3;
// Oval the drape compositor clips to: rx = min(w,h) * 0.35, ry = rx * 1.25
// (see applyColorsToImage). The framing overlay and bake MUST use the same
// geometry, or the drapes show a different crop than the user framed.
const OVAL_RX_RATIO = 0.35; // of min(frame w, frame h)
const OVAL_RY_FACTOR = 1.25; // ry = rx * this
const FRAME_OVAL_RX = Math.min(FRAME_W, FRAME_H) * OVAL_RX_RATIO;
const FRAME_OVAL_RY = FRAME_OVAL_RX * OVAL_RY_FACTOR;

// Shared Reddit post template — used by the desktop pre-filled submit URL and
// the mobile native share (where we also copy the title to the clipboard,
// since the Reddit app drops share text when files are attached).
const REDDIT_POST_TITLE = "What's my season? Draped in all 12 — need help narrowing it down";
const REDDIT_POST_BODY =
  "Hi everyone! I draped myself in all 12 seasons (using coloranalysistech.com) and I'm stuck between a few. Photos are numbered 01–12. Photos 13–18 are undertone test drapes (gold vs silver, cream vs white, warm red vs cool red) in case that helps.\n\nNMIP (no makeup in photos).\nNatural hair color: \nEye color: \n\nWhich seasons make my skin look healthiest and brightest, and which wash me out? Thank you!";

// Reddit's actual brand color + logomark (simple-icons, CC0) for the share CTAs.
const REDDIT_ORANGE = "#FF4500";
const RedditMark = ({ size = 20 }: { size?: number }) => (
  <svg role="img" viewBox="0 0 24 24" width={size} height={size} fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 0C5.373 0 0 5.373 0 12c0 3.314 1.343 6.314 3.515 8.485l-2.286 2.286C.775 23.225 1.097 24 1.738 24H12c6.627 0 12-5.373 12-12S18.627 0 12 0Zm4.388 3.199c1.104 0 1.999.895 1.999 1.999 0 1.105-.895 2-1.999 2-.946 0-1.739-.657-1.947-1.539v.002c-1.147.162-2.032 1.15-2.032 2.341v.007c1.776.067 3.4.567 4.686 1.363.473-.363 1.064-.58 1.707-.58 1.547 0 2.802 1.254 2.802 2.802 0 1.117-.655 2.081-1.601 2.531-.088 3.256-3.637 5.876-7.997 5.876-4.361 0-7.905-2.617-7.998-5.87-.954-.447-1.614-1.415-1.614-2.538 0-1.548 1.255-2.802 2.803-2.802.645 0 1.239.218 1.712.585 1.275-.79 2.881-1.291 4.64-1.365v-.01c0-1.663 1.263-3.034 2.88-3.207.188-.911.993-1.595 1.959-1.595Zm-8.085 8.376c-.784 0-1.459.78-1.506 1.797-.047 1.016.64 1.429 1.426 1.429.786 0 1.371-.369 1.418-1.385.047-1.017-.553-1.841-1.338-1.841Zm7.406 0c-.786 0-1.385.824-1.338 1.841.047 1.017.634 1.385 1.418 1.385.785 0 1.473-.413 1.426-1.429-.046-1.017-.721-1.797-1.506-1.797Zm-3.703 4.013c-.974 0-1.907.048-2.77.135-.147.015-.241.168-.183.305.483 1.154 1.622 1.964 2.953 1.964 1.33 0 2.47-.81 2.953-1.964.057-.137-.037-.29-.184-.305-.863-.087-1.795-.135-2.769-.135Z" />
  </svg>
);

// name is like 'Bright Spring' — last word is the season, the rest the subseason.
const parsePhotoName = (name: string) => {
  const parts = name.split(" ");
  return { season: parts[parts.length - 1], subseason: parts.slice(0, -1).join(" ") };
};

// Season-then-subseason order, matching the numbered filenames below so
// Reddit's multi-upload carousel stays in a sensible order.
const sortPhotosForShare = <T extends { name: string }>(photos: T[]): T[] =>
  [...photos].sort((a, b) => {
    const aUndertone = isUndertonePhoto(a.name);
    const bUndertone = isUndertonePhoto(b.name);
    if (aUndertone !== bUndertone) return aUndertone ? 1 : -1;
    if (aUndertone && bUndertone) {
      return (
        UNDERTONE_DRAPES.findIndex((d) => d.name === a.name) -
        UNDERTONE_DRAPES.findIndex((d) => d.name === b.name)
      );
    }
    const ap = parsePhotoName(a.name);
    const bp = parsePhotoName(b.name);
    return ap.season.localeCompare(bp.season) || ap.subseason.localeCompare(bp.subseason);
  });

// Format: 01_Season_Subseason.png (e.g., 01_Spring_Bright.png)
const shareFileName = (name: string, i: number) => {
  const { season, subseason } = parsePhotoName(name);
  return `${String(i + 1).padStart(2, "0")}_${season}_${subseason.replace(/\s+/g, "_")}.png`;
};

// Classic undertone drape tests, shown as three pairs (gold vs silver,
// cream vs pure white, warm red vs cool red). Rendered after the 12 seasons
// and appended to the ZIP/share as photos 13–18.
const UNDERTONE_DRAPES: { name: string; colors: string[] }[] = [
  { name: "Gold Undertone", colors: ["#F7E7B4", "#EFCF6F", "#D4A937", "#B8860B", "#9C7A1E", "#7B5E14"] },
  { name: "Silver Undertone", colors: ["#F4F6F8", "#DDE1E6", "#C0C6CE", "#A3ABB5", "#848D99", "#67707C"] },
  { name: "Cream Undertone", colors: ["#FFF9EC", "#FBF1DC", "#F6E8C9", "#F0DDB4", "#EAD3A2", "#E2C68D"] },
  { name: "White Undertone", colors: ["#FFFFFF", "#FAFBFD", "#F2F5F9", "#EAF0F6", "#E2E9F2", "#D9E2EE"] },
  { name: "Warm Red Undertone", colors: ["#FF6F4E", "#F65E3B", "#E8502C", "#D94420", "#C43A17", "#AC3110"] },
  { name: "Cool Red Undertone", colors: ["#E84A78", "#D63864", "#C22753", "#AB1E46", "#92163A", "#7A102F"] },
];
const isUndertonePhoto = (name: string) => name.endsWith(" Undertone");

// Define types for our seasonal photos
type SeasonalPhoto = {
  name: string;
  image: string;
  colors: string[];
  originalColors: string[];
};

function DrapingQueryState({onChange}: {onChange: (params: URLSearchParams) => void}) {
  const params = useSearchParams();
  useEffect(()=>onChange(new URLSearchParams(params.toString())),[params,onChange]);
  return null;
}

export default function ColorAnalysisUploadPhotoContent() {
  const router = useRouter();
  const params = useParams();
  const locale = typeof params?.locale === "string" ? params.locale : "en";
  const [photoSrc, setPhotoSrc] = useState<string | null>(null);
  const [seasonalPhotos, setSeasonalPhotos] = useState<SeasonalPhoto[]>([]);
  const [opened, { open, close }] = useDisclosure(false);
  const [redditModalOpened, { open: openRedditModal, close: closeRedditModal }] = useDisclosure(false);

  // Native share (mobile): pre-build the 12 photos as Files so the share call
  // happens inside the tap's user-activation window (iOS kills navigator.share
  // if too much async work runs between the tap and the call).
  const tFreeDrapes = useTranslations("freeDrapes");
  const [shareFiles, setShareFiles] = useState<File[] | null>(null);
  useEffect(() => {
    if (seasonalPhotos.length === 0) {
      setShareFiles(null);
      return;
    }
    let cancelled = false;
    (async () => {
      const sorted = sortPhotosForShare(seasonalPhotos);
      const files = await Promise.all(
        sorted.map(async (photo, i) => {
          const blob = await (await fetch(photo.image)).blob();
          return new File([blob], shareFileName(photo.name, i), { type: "image/png" });
        })
      );
      if (!cancelled) setShareFiles(files);
    })();
    return () => {
      cancelled = true;
    };
  }, [seasonalPhotos]);

  // Touch check keeps this off desktop browsers (macOS Chrome passes canShare
  // but its share sheet has no Reddit app — the manual flow is better there).
  const canNativeShare =
    shareFiles !== null &&
    typeof navigator !== "undefined" &&
    typeof navigator.canShare === "function" &&
    navigator.canShare({ files: shareFiles }) &&
    navigator.maxTouchPoints > 0;

  const handleNativeShare = async () => {
    if (!shareFiles) return;
    trackCtaClick("vd_share_native_open");
    // Copy the title first — clipboard needs the user gesture too, and the
    // title should survive even if they cancel the share sheet.
    try {
      await navigator.clipboard?.writeText(REDDIT_POST_TITLE);
    } catch {
      // Clipboard is best-effort; the share sheet still carries title/text.
    }
    try {
      await navigator.share({ files: shareFiles, title: REDDIT_POST_TITLE, text: REDDIT_POST_BODY });
      trackCtaClick("vd_share_native_done");
      notifications.show({
        title: "Photos handed off",
        message: "Your post title is copied — paste it into Reddit's title field.",
        color: "green",
      });
    } catch {
      // User closed the share sheet — not an error.
    }
  };
  const isMobile = useMediaQuery("(max-width: 48em)");
  const [isCameraActive, setIsCameraActive] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [blobUrls, setBlobUrls] = useState<string[]>([]);
  // Live camera preview always uses the full-circle drape overlay.
  const isFullCircle = true;
  const [hasDownloadedAll, setHasDownloadedAll] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Sticky "Unlock" bar: hidden until the photo has been draped for a beat, dismissable.
  const [showUnlockBar, setShowUnlockBar] = useState(false);
  const [unlockBarDismissed, setUnlockBarDismissed] = useState(false);
  const [unlockBarVisible, setUnlockBarVisible] = useState(false); // drives the slide-in transition

  // Price shown on the AI Color Analysis upsell CTAs — purely display,
  // resolved server-side from the same bucket checkout charges.
  const [priceDisplay, setPriceDisplay] = useState<string | null>(null);
  useEffect(() => {
    fetch(`/api/geo-pricing?bucket=${getPriceBucket().id}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => d?.priceDisplay && setPriceDisplay(d.priceDisplay))
      .catch(() => {});
  }, []);

  // Framing step: user pans/zooms their photo into the oval before draping.
  const [rawPhoto, setRawPhoto] = useState<string | null>(null);
  const [isFraming, setIsFraming] = useState(false);
  const [frameScale, setFrameScale] = useState(1);
  const [frameOffset, setFrameOffset] = useState({ x: 0, y: 0 });
  // Natural pixel size of rawPhoto — needed to render the preview at the exact
  // cover-fit size the bake assumes, instead of the image's natural size.
  const [rawPhotoSize, setRawPhotoSize] = useState<{ w: number; h: number } | null>(null);

  // Re-encode an image data URL as a downscaled JPEG so it fits in sessionStorage.
  // A full-res mobile selfie as PNG base64 easily blows past the ~5MB quota, which
  // used to make setItem throw and silently kill the Unlock navigation on mobile.
  const toStorableDataUrl = (src: string, maxDim = 1400, quality = 0.85): Promise<string> =>
    new Promise((resolve) => {
      const img = new window.Image();
      img.onload = () => {
        const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          resolve(src);
          return;
        }
        ctx.drawImage(img, 0, 0, w, h);
        try {
          resolve(canvas.toDataURL("image/jpeg", quality));
        } catch {
          resolve(src);
        }
      };
      img.onerror = () => resolve(src);
      img.src = src;
    });

  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const checkoutInFlight = useRef(false);
  const pendingCheckout = useRef<{ image: string; id: string } | null>(null);

  // The offer names the price and opens payment directly; free drapes stay available.
  const handleUpgradeClick = async (source: string) => {
    if (!photoSrc || checkoutInFlight.current) return;
    checkoutInFlight.current = true;
    setCheckoutLoading(true);
    setCheckoutError(null);
    const startedAt = Date.now();
    trackCtaClick(source);
    const bucket = getPriceBucket();
    trackCheckoutStart("color-analysis", { bucket: bucket.id, price_cents: bucket.priceCents, flow: "draping_direct_v1" });
    try {
      const storable = await toStorableDataUrl(photoSrc);
      sessionStorage.setItem("colorAnalysisImage", storable);
      sessionStorage.setItem("vdCheckoutPhoto", storable);
      const previous = pendingCheckout.current?.image === photoSrc ? pendingCheckout.current.id : undefined;
      const url = await createColorAnalysisCheckout({
        imageData: storable, bucketId: bucket.id, email: getStoredLeadEmail(),
        cancelPagePath: window.location.pathname, prePaymentSessionId: previous,
        onPhotoReady: (id) => { pendingCheckout.current = { image: photoSrc, id }; },
      });
      trackGizmo("checkout_redirect", { tool: "color-analysis", flow: "draping_direct_v1", elapsed_ms: Date.now() - startedAt });
      window.location.href = url;
    } catch {
      checkoutInFlight.current = false;
      setCheckoutLoading(false);
      setCheckoutError("We couldn't open checkout. Please try again. Your free drapes are still available.");
      trackGizmo("checkout_error", { tool: "color-analysis", flow: "draping_direct_v1" });
    }
  };

  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const photoCanvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const videoBoxRef = useRef<HTMLDivElement>(null);
  const grayDrapeCanvasRef = useRef<HTMLCanvasElement>(null);
  const animationFrameRef = useRef<number | null>(null);

  // Clean up camera resources when component unmounts
  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
    };
  }, []);

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user" },
        audio: false,
      });

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        streamRef.current = stream;
        setIsCameraActive(true);
      }
    } catch (err) {
      notifications.show({
        title: "Camera Error",
        message: "Unable to access your camera. Please check permissions.",
        color: "red",
      });
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    setIsCameraActive(false);
  };

  // Enter the framing step: normalize EXIF rotation once, then let the user
  // pan/zoom the result into the oval. We bake off the normalized data URL so
  // what they see in the preview is exactly what gets draped.
  const startFraming = async (imageSrc: string) => {
    trackGizmo("draping_photo_upload", { tool: "draping", mode: "personal" });
    try {
      const response = await fetch(imageSrc);
      const blob = await response.blob();
      const bitmap = await createImageBitmap(blob, { imageOrientation: "from-image" });
      const canvas = document.createElement("canvas");
      canvas.width = bitmap.width;
      canvas.height = bitmap.height;
      const ctx = canvas.getContext("2d");
      let normalized = imageSrc;
      if (ctx) {
        ctx.drawImage(bitmap, 0, 0);
        normalized = canvas.toDataURL("image/png");
      }
      bitmap.close();
      setRawPhoto(normalized);
    } catch {
      // Fallback: use the source as-is (EXIF may be off, but the crop still works)
      setRawPhoto(imageSrc);
    }
    setRawPhotoSize(null); // re-measured by the preview img's onLoad
    setFrameScale(1);
    setFrameOffset({ x: 0, y: 0 });
    setIsFraming(true);
    setIsProcessing(false);
  };

  // Free digital drapes decline path: the result page stores the user's
  // selfie in sessionStorage before sending them here with ?src=result_decline.
  // Auto-load it through the exact same startFraming path handleFileUpload
  // uses, so it gets framed + draped without a re-upload.
  const [searchParams, setSearchParams] = useState(()=>new URLSearchParams());
  const fdAutoloadFired = useRef(false);
  const [fdNudgeDismissed, setFdNudgeDismissed] = useState(false);
  useEffect(() => {
    if (searchParams.get("cancelled") === "true" && !fdAutoloadFired.current && !photoSrc && !rawPhoto) {
      const saved = sessionStorage.getItem("vdCheckoutPhoto");
      if (saved) {
        fdAutoloadFired.current = true;
        setPhotoSrc(saved);
        setIsProcessing(true);
        void processImage(saved);
        return;
      }
    }
    const src = searchParams.get("src");
    // result_decline = free-drapes decline flow; guided = arrival from the
    // free Guided Color Analysis result. Both carry the selfie via the same
    // sessionStorage key.
    const allowed =
      (src === "result_decline" && FREE_DRAPES_V1) ||
      (src === "guided" && QUICK_ANALYSIS_V1);
    if (!allowed) return;
    if (fdAutoloadFired.current) return;
    if (photoSrc || rawPhoto) return; // already have a photo loaded

    const stored = sessionStorage.getItem("colorAnalysisImage");
    if (!stored) return;

    fdAutoloadFired.current = true;
    trackCtaClick(src === "guided" ? "qa_vd_autoload" : "fd_vd_autoload");
    startFraming(stored);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, photoSrc, rawPhoto]);

  // The base "cover" scale that makes the image fill the FRAME_W x FRAME_H box
  // at frameScale === 1 (same as CSS object-fit: cover). Shared by preview + bake.
  const coverScale = (imgW: number, imgH: number) => Math.max(FRAME_W / imgW, FRAME_H / imgH);

  // Bake the current pan/zoom into an oval-masked data URL, mirroring the preview
  // transform exactly on a higher-res offscreen canvas.
  const renderFramedOval = (src: string): Promise<string> =>
    new Promise((resolve) => {
      const img = new window.Image();
      img.onload = () => {
        const outW = FRAME_W * FRAME_QUALITY;
        const outH = FRAME_H * FRAME_QUALITY;
        const canvas = document.createElement("canvas");
        canvas.width = outW;
        canvas.height = outH;
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          resolve(src);
          return;
        }

        // Gray backdrop behind the oval, matching getOvalMaskedImage.
        ctx.fillStyle = "#E0E0E0";
        ctx.fillRect(0, 0, outW, outH);

        // Oval clip — same geometry as the drape compositor's clip, so the
        // drapes reveal exactly what the user framed.
        ctx.save();
        ctx.beginPath();
        ctx.ellipse(
          outW / 2,
          outH / 2,
          FRAME_OVAL_RX * FRAME_QUALITY,
          FRAME_OVAL_RY * FRAME_QUALITY,
          0,
          0,
          2 * Math.PI
        );
        ctx.closePath();
        ctx.clip();

        // Same geometry as the CSS preview: base cover fit, then user scale,
        // then user pan (offset is in preview CSS px -> scale up by QUALITY).
        const base = coverScale(img.width, img.height);
        const drawScale = base * frameScale * FRAME_QUALITY;
        const drawW = img.width * drawScale;
        const drawH = img.height * drawScale;
        const dx = (outW - drawW) / 2 + frameOffset.x * FRAME_QUALITY;
        const dy = (outH - drawH) / 2 + frameOffset.y * FRAME_QUALITY;
        ctx.drawImage(img, dx, dy, drawW, drawH);
        ctx.restore();

        try {
          resolve(canvas.toDataURL("image/png"));
        } catch {
          resolve(src);
        }
      };
      img.onerror = () => resolve(src);
      img.src = src;
    });

  // Confirm framing -> bake -> run the 12-season drape (unchanged downstream).
  const confirmFraming = async () => {
    if (!rawPhoto) return;
    setIsProcessing(true);
    setIsFraming(false);
    const masked = await renderFramedOval(rawPhoto);
    setPhotoSrc(masked);
    setRawPhoto(null);
    processImage(masked);
  };

  const cancelFraming = () => {
    setIsFraming(false);
    setRawPhoto(null);
    setRawPhotoSize(null);
    setFrameScale(1);
    setFrameOffset({ x: 0, y: 0 });
  };

  // --- Framing pan/zoom interaction (drag to pan, pinch or slider to zoom) ---
  const dragState = useRef<{ x: number; y: number } | null>(null);
  const activePointers = useRef<Map<number, { x: number; y: number }>>(new Map());
  const pinchState = useRef<{ dist: number; scale: number } | null>(null);

  const pointerDist = () => {
    const pts = Array.from(activePointers.current.values());
    if (pts.length < 2) return 0;
    return Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
  };

  const handleFramePointerDown = (e: React.PointerEvent) => {
    (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
    activePointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (activePointers.current.size === 2) {
      pinchState.current = { dist: pointerDist(), scale: frameScale };
      dragState.current = null;
    } else {
      dragState.current = { x: e.clientX, y: e.clientY };
    }
  };

  const handleFramePointerMove = (e: React.PointerEvent) => {
    if (!activePointers.current.has(e.pointerId)) return;
    activePointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });

    if (pinchState.current && activePointers.current.size >= 2) {
      const dist = pointerDist();
      if (pinchState.current.dist > 0) {
        const next = pinchState.current.scale * (dist / pinchState.current.dist);
        setFrameScale(Math.min(MAX_FRAME_SCALE, Math.max(MIN_FRAME_SCALE, next)));
      }
      return;
    }

    if (dragState.current) {
      const dx = e.clientX - dragState.current.x;
      const dy = e.clientY - dragState.current.y;
      dragState.current = { x: e.clientX, y: e.clientY };
      setFrameOffset((prev) => ({ x: prev.x + dx, y: prev.y + dy }));
    }
  };

  const handleFramePointerUp = (e: React.PointerEvent) => {
    activePointers.current.delete(e.pointerId);
    if (activePointers.current.size < 2) pinchState.current = null;
    if (activePointers.current.size === 0) dragState.current = null;
  };

  // Add cleanup for blob URLs
  useEffect(() => {
    return () => {
      // Cleanup any blob URLs when component unmounts
      if (photoSrc?.startsWith("blob:")) {
        URL.revokeObjectURL(photoSrc);
      }
      blobUrls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [photoSrc, blobUrls]);

  // Sticky Unlock bar: arm an ~11s timer once the photo is actually draped,
  // then reveal + slide it up. Dismiss (or Start Over) suppresses / re-arms it.
  useEffect(() => {
    const draped = Boolean(photoSrc) && seasonalPhotos.length > 0;
    if (!draped || unlockBarDismissed) {
      setShowUnlockBar(false);
      setUnlockBarVisible(false);
      return;
    }
    const showTimer = setTimeout(() => setShowUnlockBar(true), 11000);
    return () => clearTimeout(showTimer);
  }, [photoSrc, seasonalPhotos.length, unlockBarDismissed]);

  // Trigger the slide-in one tick after the bar mounts (translateY 100% -> 0).
  useEffect(() => {
    if (!showUnlockBar) return;
    const raf = requestAnimationFrame(() => setUnlockBarVisible(true));
    return () => cancelAnimationFrame(raf);
  }, [showUnlockBar]);

  // Modify takePhoto to include background removal
  const takePhoto = async () => {
    if (videoRef.current && canvasRef.current) {
      setIsProcessing(true);
      const video = videoRef.current;
      const canvas = canvasRef.current;

      // Set canvas dimensions to match video
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      // Draw the current video frame to the canvas
      const context = canvas.getContext("2d");
      if (context) {
        // Draw the current video frame with object-fit: cover logic
        if (video.videoWidth && video.videoHeight) {
          const videoAspect = video.videoWidth / video.videoHeight;
          const canvasAspect = canvas.width / canvas.height;
          let sx = 0,
            sy = 0,
            sWidth = video.videoWidth,
            sHeight = video.videoHeight;
          if (videoAspect > canvasAspect) {
            sWidth = video.videoHeight * canvasAspect;
            sx = (video.videoWidth - sWidth) / 2;
          } else {
            sHeight = video.videoWidth / canvasAspect;
            sy = (video.videoHeight - sHeight) / 2;
          }

          // Draw the video frame without mirroring
          context.save();
          context.drawImage(video, sx, sy, sWidth, sHeight, 0, 0, canvas.width, canvas.height);
          context.restore();
        }

        // Convert canvas to data URL
        const dataUrl = canvas.toDataURL("image/png");

        // Close camera and hand off to the framing step (pan/zoom into oval).
        close();
        stopCamera();

        try {
          await startFraming(dataUrl);
        } catch (error) {
          console.error("Error processing image:", error);
          setIsProcessing(false);
          notifications.show({
            title: "Error",
            message: "Failed to process image. Please try again.",
            color: "red",
          });
        }
      }
    }
  };

  const openCamera = () => {
    open();
    setTimeout(() => {
      startCamera();
    }, 100);
  };

  const handleCloseCamera = () => {
    stopCamera();
    close();
  };

  // Update: applyColorsToImage now takes a seasonLabel
  const applyColorsToImage = async (
    image: HTMLImageElement,
    colors: string[],
    seasonLabel?: string
  ): Promise<string> => {
    // Create a new canvas for each season
    const canvas = document.createElement("canvas");
    canvas.width = image.width;
    canvas.height = image.height;

    const ctx = canvas.getContext("2d");
    if (!ctx) return "";

    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;

    // --- Background: full-width horizontal color stripes (Vivaldi style) ---
    // Draw evenly-stacked bands so each season's palette fills the whole frame.
    const stripeHeight = canvas.height / colors.length;
    colors.forEach((color, i) => {
      ctx.fillStyle = color;
      // Overlap by 1px to avoid hairline seams from sub-pixel rounding.
      ctx.fillRect(0, Math.floor(i * stripeHeight), canvas.width, Math.ceil(stripeHeight) + 1);
    });

    // --- Face: clip an oval and draw the (already oval-masked) face over the stripes ---
    // Same oval geometry as getOvalMaskedImage so the face lands in a clean, centered oval.
    // Re-clipping to that oval hides the grey corners of the masked source image.
    const ovalRx = Math.min(canvas.width, canvas.height) * 0.35;
    const ovalRy = ovalRx * 1.25;
    ctx.save();
    ctx.beginPath();
    ctx.ellipse(centerX, centerY, ovalRx, ovalRy, 0, 0, 2 * Math.PI);
    ctx.closePath();
    ctx.clip();
    ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
    ctx.restore();

    // Add logo and text to bottom right (watermark)
    const logo = new Image();
    logo.src = "/palette.svg";

    // Wait for logo to load before drawing
    return new Promise((resolve) => {
      logo.onload = () => {
        // Calculate dimensions for logo and text
        const logoSize = canvas.width * 0.04;
        const padding = logoSize * 0.3;
        const textSize = logoSize * 0.8;
        const boxPadding = logoSize * 0.2;

        // --- Top-center: prominent season pill (Vivaldi style) ---
        if (seasonLabel) {
          const pillTextSize = canvas.width * 0.05;
          const pillPadX = pillTextSize * 0.9;
          const pillPadY = pillTextSize * 0.5;
          const label = seasonLabel.replace(/\b\w/g, (c) => c.toUpperCase());
          ctx.font = `600 ${pillTextSize}px Arial, sans-serif`;
          const pillTextWidth = ctx.measureText(label).width;
          const pillWidth = pillTextWidth + pillPadX * 2;
          const pillHeight = pillTextSize + pillPadY * 2;
          const pillX = (canvas.width - pillWidth) / 2; // centered
          const pillY = canvas.height * 0.05; // near the top, with breathing room
          ctx.fillStyle = "rgba(255, 255, 255, 0.92)";
          ctx.beginPath();
          ctx.roundRect(pillX, pillY, pillWidth, pillHeight, pillHeight / 2);
          ctx.fill();
          ctx.fillStyle = "#1a1a1a";
          ctx.textAlign = "center";
          ctx.textBaseline = "middle";
          ctx.font = `600 ${pillTextSize}px Arial, sans-serif`;
          ctx.fillText(label, canvas.width / 2, pillY + pillHeight / 2);
        }

        // --- Bottom-right: ColorAnalysisTech logo/text ---
        ctx.font = `${textSize}px Arial`;
        const text = "ColorAnalysisTech";
        const textMetrics = ctx.measureText(text);
        const textWidth = textMetrics.width;
        const boxWidth = logoSize + padding + textWidth + boxPadding * 2;
        const boxHeight = logoSize + boxPadding * 2;
        const boxX = canvas.width - boxWidth - padding; // right side
        const boxY = canvas.height - boxHeight - padding; // bottom
        ctx.fillStyle = "rgba(0, 0, 0, 0.3)";
        ctx.beginPath();
        ctx.roundRect(boxX, boxY, boxWidth, boxHeight, 8);
        ctx.fill();
        ctx.drawImage(logo, boxX + boxPadding, boxY + boxPadding, logoSize, logoSize);
        ctx.fillStyle = "white";
        ctx.textAlign = "left";
        ctx.textBaseline = "middle";
        ctx.fillText(text, boxX + boxPadding + logoSize + padding, boxY + boxHeight / 2);

        resolve(canvas.toDataURL("image/png"));
      };
    });
  };

  // Update processImage to handle async applyColorsToImage
  const processImage = async (imageSrc: string): Promise<void> => {
    const startedAt = Date.now();
    setHasDownloadedAll(false);
    const img = new Image();
    img.onload = async () => {
      try {
        // Process all subseasons
        const seasonPromises = Object.entries(colorSeasons).flatMap(([season, data]) =>
          Object.entries(data.subseasons).map(async ([subseason, subseasonData]) => {
            const seasonImg = new Image();
            await new Promise((resolve) => {
              seasonImg.onload = resolve;
              seasonImg.src = imageSrc;
            });

            const colors = subseasonData.primaryColors.map((color) => color.hex);
            const processedImage = await applyColorsToImage(
              seasonImg,
              colors,
              `${subseason.replace(/_/g, " ")} ${season}`
            );
            return {
              name: `${subseason.replace(/_/g, " ")} ${season}`,
              image: processedImage,
              colors: colors,
              originalColors: colors,
            };
          })
        );

        const undertonePromises = UNDERTONE_DRAPES.map(async (drape) => {
          const undertoneImg = new Image();
          await new Promise((resolve) => {
            undertoneImg.onload = resolve;
            undertoneImg.src = imageSrc;
          });

          const processedImage = await applyColorsToImage(undertoneImg, drape.colors, drape.name);
          return {
            name: drape.name,
            image: processedImage,
            colors: drape.colors,
            originalColors: drape.colors,
          };
        });

        const [allSeasonImages, undertoneImages] = await Promise.all([
          Promise.all(seasonPromises),
          Promise.all(undertonePromises),
        ]);
        setSeasonalPhotos([...allSeasonImages, ...undertoneImages]);
        trackGizmo("draping_result_ready", { tool: "draping", mode: "personal", elapsed_ms: Date.now() - startedAt });
        setIsProcessing(false);
      } catch (error) {
        console.error("Error processing seasons:", error);
        trackGizmo("draping_result_error", { tool: "draping", reason: "render_failed" });
        setIsProcessing(false);
      }
    };
    img.src = imageSrc;
  };

  // Update handleColorClick to handle async applyColorsToImage
  const handleColorClick = async (seasonIndex: number, color: string) => {
    const img = new Image();
    img.onload = async () => {
      const processedImage = await applyColorsToImage(
        img,
        Array(seasonalPhotos[seasonIndex].originalColors.length).fill(color),
        seasonalPhotos[seasonIndex].name
      );
      setSeasonalPhotos((prev) =>
        prev.map((item, index) => {
          if (index === seasonIndex) {
            return {
              ...item,
              image: processedImage,
              colors: Array(item.originalColors.length).fill(color),
            };
          }
          return item;
        })
      );
    };
    img.src = photoSrc!;
  };

  // Update handleReset to handle async applyColorsToImage
  const handleReset = async (seasonIndex: number) => {
    const img = new Image();
    img.onload = async () => {
      const processedImage = await applyColorsToImage(
        img,
        seasonalPhotos[seasonIndex].originalColors,
        seasonalPhotos[seasonIndex].name
      );
      setSeasonalPhotos((prev) =>
        prev.map((item, index) => {
          if (index === seasonIndex) {
            return {
              ...item,
              image: processedImage,
              colors: [...item.originalColors],
            };
          }
          return item;
        })
      );
    };
    img.src = photoSrc!;
  };

  const handleStartOver = () => {
    setPhotoSrc(null);
    setCheckoutError(null);
    pendingCheckout.current = null;
    sessionStorage.removeItem("vdCheckoutPhoto");
    setSeasonalPhotos([]);
    setHasDownloadedAll(false);
    // Re-arm the sticky bar and clear any in-progress framing for the next photo.
    setShowUnlockBar(false);
    setUnlockBarVisible(false);
    setUnlockBarDismissed(false);
    setRawPhoto(null);
    setIsFraming(false);
    setFrameScale(1);
    setFrameOffset({ x: 0, y: 0 });
  };

  // Update the useEffect for video preview
  useEffect(() => {
    if (!opened || !isCameraActive) return;
    const box = videoBoxRef.current;
    const canvas = grayDrapeCanvasRef.current;
    const video = videoRef.current;
    let running = true;
    function drawLoop() {
      if (!running) return;
      if (box && canvas && video) {
        canvas.width = box.clientWidth;
        canvas.height = box.clientHeight;
        const ctx = canvas.getContext("2d");
        if (ctx) {
          ctx.clearRect(0, 0, canvas.width, canvas.height);

          // Draw the current video frame with object-fit: cover logic
          if (video.videoWidth && video.videoHeight) {
            const videoAspect = video.videoWidth / video.videoHeight;
            const canvasAspect = canvas.width / canvas.height;
            let sx = 0,
              sy = 0,
              sWidth = video.videoWidth,
              sHeight = video.videoHeight;
            if (videoAspect > canvasAspect) {
              sWidth = video.videoHeight * canvasAspect;
              sx = (video.videoWidth - sWidth) / 2;
            } else {
              sHeight = video.videoWidth / canvasAspect;
              sy = (video.videoHeight - sHeight) / 2;
            }

            // Draw the video frame without mirroring
            ctx.save();
            ctx.drawImage(video, sx, sy, sWidth, sHeight, 0, 0, canvas.width, canvas.height);
            ctx.restore();
          }

          // Draw the drape overlay with current circle style
          drawDrapeOverlay(ctx, canvas.width, canvas.height, {
            color: "gray",
            segmentCount: 12,
            isFullCircle: isFullCircle,
          });
        }
      }
      animationFrameRef.current = requestAnimationFrame(drawLoop);
    }
    drawLoop();
    window.addEventListener("resize", drawLoop);
    return () => {
      running = false;
      if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
      window.removeEventListener("resize", drawLoop);
    };
  }, [opened, isCameraActive, isFullCircle]);

  // Function to download all images as a ZIP
  const handleDownloadAll = async () => {
    if (seasonalPhotos.length === 0) return;

    setIsProcessing(true);
    setHasDownloadedAll(false);
    try {
      const zip = new JSZip();

      const sortedPhotos = sortPhotosForShare(seasonalPhotos);
      await Promise.all(
        sortedPhotos.map(async (photo, i) => {
          const response = await fetch(photo.image);
          const blob = await response.blob();
          zip.file(shareFileName(photo.name, i), blob);
        })
      );

      // Generate and download ZIP
      const content = await zip.generateAsync({ type: "blob" });
      const url = URL.createObjectURL(content);
      const link = document.createElement("a");
      link.href = url;
      link.download = "color_analysis_tech_results.zip";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      setHasDownloadedAll(true);
    } catch (error) {
      console.error("Error creating ZIP:", error);
      setHasDownloadedAll(false);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsProcessing(true);
    try {
      // Create a data URL from the file, then hand off to the framing step.
      const reader = new FileReader();
      reader.onload = async (e) => {
        const dataUrl = e.target?.result as string;
        await startFraming(dataUrl);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error("Error processing file:", error);
      setIsProcessing(false);
      notifications.show({
        title: "Error",
        message: "Failed to process image. Please try again.",
        color: "red",
      });
    }
    // Allow re-selecting the same file later (onChange won't fire otherwise).
    event.target.value = "";
  };

  return (
    <Stack gap="xl">
      <Suspense fallback={null}><DrapingQueryState onChange={setSearchParams} /></Suspense>
      <LoadingOverlay
        visible={isProcessing}
        pos="fixed"
        top={0}
        left={0}
        right={0}
        bottom={0}
        zIndex={1000}
        c="wumpus"
        overlayProps={{ blur: 3, opacity: 0.99 }}
        loaderProps={{ size: "xl", type: "bars", color: "wumpus" }}
      />

      <Title order={1} ta="center">
        Virtual Color Draping — Upload a Photo, See Every Season
      </Title>
      <Text size="md" ta="center" c="dimmed" maw={620} mx="auto">
        Try color draping online: upload a photo and compare all 12 seasonal palettes for free.
        Keep the same photo and lighting while you explore which colors you prefer beside your face.
      </Text>

      {isFraming && rawPhoto ? (
        <Paper
          withBorder
          p={{ base: "md", md: "xl" }}
          radius="md"
          maw={480}
          mx="auto"
          style={{ borderColor: "var(--mantine-color-wumpus-4)", borderWidth: "2px" }}
          bg="wumpus.0"
        >
          <Stack align="center" gap="md">
            <Text fw={600} ta="center">
              Fit your face in the oval
            </Text>
            <Text fz="sm" c="dimmed" ta="center">
              Drag to move your photo and use the slider (or pinch) to zoom, so your face sits nicely inside the oval.
            </Text>

            {/* Preview: image transformed by pan/zoom, oval cut-out on top. */}
            <Box
              onPointerDown={handleFramePointerDown}
              onPointerMove={handleFramePointerMove}
              onPointerUp={handleFramePointerUp}
              onPointerCancel={handleFramePointerUp}
              style={{
                position: "relative",
                width: FRAME_W,
                height: FRAME_H,
                maxWidth: "100%",
                overflow: "hidden",
                borderRadius: 8,
                background: "#E0E0E0",
                touchAction: "none",
                cursor: "grab",
                userSelect: "none",
              }}
            >
              {/* The image, positioned "cover" then transformed by user pan/zoom. */}
              <img
                src={rawPhoto}
                alt="Your photo"
                draggable={false}
                onLoad={(e) => {
                  const el = e.currentTarget;
                  if (el.naturalWidth && el.naturalHeight) {
                    setRawPhotoSize({ w: el.naturalWidth, h: el.naturalHeight });
                  }
                }}
                style={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  // Render at the exact cover-fit size renderFramedOval bakes with,
                  // so preview px === bake px. Until measured, object-fit cover
                  // looks identical at scale 1.
                  ...(rawPhotoSize
                    ? {
                        width: rawPhotoSize.w * coverScale(rawPhotoSize.w, rawPhotoSize.h),
                        height: rawPhotoSize.h * coverScale(rawPhotoSize.w, rawPhotoSize.h),
                      }
                    : { width: "100%", height: "100%", objectFit: "cover" as const }),
                  transform: `translate(-50%, -50%) translate(${frameOffset.x}px, ${frameOffset.y}px) scale(${frameScale})`,
                  transformOrigin: "center",
                  pointerEvents: "none",
                }}
              />
              {/* Oval spotlight: darken everything outside the ellipse. */}
              <svg
                width="100%"
                height="100%"
                viewBox={`0 0 ${FRAME_W} ${FRAME_H}`}
                style={{ position: "absolute", inset: 0, pointerEvents: "none" }}
              >
                <defs>
                  <mask id="frame-oval-mask">
                    <rect width={FRAME_W} height={FRAME_H} fill="white" />
                    <ellipse cx={FRAME_W / 2} cy={FRAME_H / 2} rx={FRAME_OVAL_RX} ry={FRAME_OVAL_RY} fill="black" />
                  </mask>
                </defs>
                <rect width={FRAME_W} height={FRAME_H} fill="rgba(224,224,224,0.7)" mask="url(#frame-oval-mask)" />
                <ellipse
                  cx={FRAME_W / 2}
                  cy={FRAME_H / 2}
                  rx={FRAME_OVAL_RX - 1}
                  ry={FRAME_OVAL_RY - 1}
                  fill="none"
                  stroke="var(--mantine-color-wumpus-5)"
                  strokeWidth={2}
                />
              </svg>
            </Box>

            <Group gap="xs" w="100%" maw={FRAME_W} wrap="nowrap">
              <Text fz="xs" c="dimmed">
                Zoom
              </Text>
              <Slider
                flex={1}
                min={MIN_FRAME_SCALE}
                max={MAX_FRAME_SCALE}
                step={0.01}
                value={frameScale}
                onChange={setFrameScale}
                label={null}
                color="wumpus"
              />
            </Group>

            <Group w="100%" maw={FRAME_W} grow>
              <Button variant="default" onClick={cancelFraming}>
                Choose another
              </Button>
              <Button color="wumpus" leftSection={<IconCheck size={18} />} onClick={confirmFraming}>
                Looks good
              </Button>
            </Group>
          </Stack>
        </Paper>
      ) : !photoSrc ? (
        <>
          <Paper
            withBorder
            p="xl"
            radius="md"
            pos="relative"
            maw={600}
            mx="auto"
            style={{ borderColor: "var(--mantine-color-wumpus-4)", borderWidth: "2px" }}
            bg="wumpus.0"
          >
            <Stack align="center" gap="md">
              <Text fz="sm" ta="center">
                Take a well-lit selfie against a neutral background. If possible, position your face so that your hair
                is not present in the oval, or wrap your hair in a neutral color.
              </Text>
              {!isMobile && (
                <>
                  <Button leftSection={<IconCamera size={18} />} onClick={openCamera} color="wumpus" fullWidth>
                    Use Camera
                  </Button>
                  <Divider label="or" w="100%" />
                </>
              )}
              <Button
                leftSection={<IconUpload size={18} />}
                onClick={() => fileInputRef.current?.click()}
                variant="light"
                color="wumpus"
                fullWidth
              >
                Upload Photo
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileUpload}
                style={{ display: "none" }}
              />
            </Stack>
          </Paper>
          <Text size="md" ta="center">
            This free online draping session lets you preview every seasonal color palette on your own photo. Whether
            you're exploring Light Summer, Deep Autumn, or Bright Winter, our virtual draping system gives you a clear
            visual comparison — just like a professional in-person color draping session. Try our color analysis drapes
            and find your most flattering palette instantly.
          </Text>

          <Paper
            p={{ base: "md", md: "xl" }}
            radius="lg"
            withBorder
            style={{
              borderColor: "var(--mantine-color-wumpus-2)",
              borderWidth: "2px",
              background: "linear-gradient(to bottom, var(--mantine-color-wumpus-0), var(--mantine-color-wumpus-1))",
            }}
          >
            <Stack gap="xl">
              <Title order={2} ta="center" size="h1" fw={700}>
                Not sure what your color season is?
              </Title>

              <SimpleGrid cols={{ base: 1, md: 2 }} spacing={{ base: "md", md: "xl" }}>
                <Paper
                  p="xl"
                  radius="md"
                  withBorder
                  style={{
                    background: "var(--mantine-color-white)",
                    borderColor: "var(--mantine-color-blue-2)",
                  }}
                >
                  <Stack gap="md" align="center">
                    <Box
                      style={{
                        width: 60,
                        height: 60,
                        borderRadius: "50%",
                        background: "var(--mantine-color-blue-1)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <IconBrain size={32} color="var(--mantine-color-blue-6)" />
                    </Box>
                    <Title order={3} ta="center" fw={600}>
                      Free Color Analysis Quiz
                    </Title>
                    <Text size="sm" c="dimmed" ta="center">
                      A fun, quick way to get started with color analysis
                    </Text>
                    <Button
                      variant="light"
                      component={Link}
                      color="blue"
                      size="lg"
                      fullWidth
                      leftSection={<IconBrain size={20} />}
                      href="/color-analysis-quiz"
                      mt="md"
                    >
                      Take the Quiz
                    </Button>
                  </Stack>
                </Paper>

                <Paper
                  p="xl"
                  radius="md"
                  withBorder
                  style={{
                    background: "var(--mantine-color-white)",
                    borderColor: "var(--mantine-color-violet-2)",
                  }}
                >
                  <Stack gap="md" align="center">
                    <Box
                      style={{
                        width: 60,
                        height: 60,
                        borderRadius: "50%",
                        background: "var(--mantine-color-violet-1)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <IconRobot size={32} color="var(--mantine-color-violet-6)" />
                    </Box>
                    <Title order={3} ta="center" fw={600}>
                      AI Color Analysis
                    </Title>
                    <Group justify="center" gap="xs">
                      <Badge color="violet" variant="light">
                        Premium
                      </Badge>
                      <Badge color="violet" variant="light">
                        High Quality
                      </Badge>
                    </Group>
                    <Text size="sm" c="dimmed" ta="center">
                      Get personalized results with our advanced AI technology
                    </Text>
                    <Button
                      variant="filled"
                      component={Link}
                      href="/ai-color-analysis"
                      color="violet"
                      size="lg"
                      fullWidth
                      leftSection={<IconRobot size={20} />}
                      mt="md"
                    >
                      Try AI Analysis
                    </Button>
                  </Stack>
                </Paper>
              </SimpleGrid>
            </Stack>
          </Paper>
        </>
      ) : (
        <Stack>
          {/* Control Panel Paper */}
          <Paper withBorder p="md" radius="md" bg="gray.0">
            <Flex
              justify={{ base: "space-evenly", sm: "space-between" }}
              gap="md"
              align="center"
              direction={{ base: "column", sm: "row" }}
            >
              {/* Left side: Primary actions */}
              <Group>
                <Button variant="light" leftSection={<IconCamera size={18} />} onClick={handleStartOver}>
                  New Photo
                </Button>
                {seasonalPhotos.length > 0 && (
                  <Button
                    variant="filled"
                    color="green"
                    onClick={handleDownloadAll}
                    leftSection={<IconDownload size={18} />}
                  >
                    Download All
                  </Button>
                )}
              </Group>

              {/* Right side: Share to community */}
              {seasonalPhotos.length > 0 && (
                <Button
                  variant="filled"
                  color="orange"
                  leftSection={<IconUsers size={18} />}
                  onClick={() => {
                    trackCtaClick("vd_share_reddit_open");
                    openRedditModal();
                  }}
                >
                  Get a Second Opinion
                </Button>
              )}
            </Flex>
          </Paper>

          {checkoutError && <Alert color="red" role="alert">{checkoutError}</Alert>}

          {/* Simple Insight Bar - ONE clear message */}
          <Paper p="md" radius="md" bg="violet.0" style={{ border: "1px solid var(--mantine-color-violet-2)" }}>
            <Flex align="center" justify="space-between" gap="md" wrap="wrap">
              <Text fz="sm" c="violet.8">
                <Text span fw={600}>Want help choosing?</Text> — get an AI color report, palette and styling recommendations
              </Text>
              <Button
                size="sm"
                color="violet"
                variant="filled"
                rightSection={<IconArrowRight size={16} />}
                onClick={() => handleUpgradeClick("vd_ai_analysis_toolbar")}
                loading={checkoutLoading}
              >
                {`Get My AI Analysis — ${priceDisplay ?? "$24.99"}`}
              </Button>
            </Flex>
          </Paper>

          {/* Arrival nudge for the free-drapes decline handoff from the result page */}
          {FREE_DRAPES_V1 &&
            searchParams.get("src") === "result_decline" &&
            seasonalPhotos.length > 0 &&
            !fdNudgeDismissed && (
            <Alert
              color="violet"
              radius="md"
              variant="light"
              withCloseButton
              onClose={() => setFdNudgeDismissed(true)}
              title={tFreeDrapes("nudgeTitle")}
            >
              <Stack gap="sm">
                <Text fz="sm">{tFreeDrapes("nudgeBody")}</Text>
                <Button
                  variant="light"
                  color="violet"
                  size="sm"
                  onClick={() => {
                    trackCtaClick("vd_share_reddit_open");
                    openRedditModal();
                  }}
                >
                  {tFreeDrapes("nudgeCta")}
                </Button>
              </Stack>
            </Alert>
          )}

          {/* Results Paper - All 12 seasons */}
          <Paper withBorder>
            <Stack gap="xl">
              {Object.entries(colorSeasons).map(([season, _]) => (
                <Stack key={season} gap="md">
                  <Divider
                    label={
                      <Text c="dimmed" fz="lg">
                        {season}
                      </Text>
                    }
                  />
                  <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="md">
                    {seasonalPhotos
                      .filter((photo) => photo.name.endsWith(season))
                      .map((item, index) => (
                        <Paper key={index} withBorder p="xs" radius="md">
                          <MantineImage src={item.image} alt={`You in ${item.name} colors`} radius="md" />
                          <Flex gap="0" justify="space-evenly" align="center" wrap="nowrap" pt="xs">
                            {[...item.originalColors].reverse().map((color, colorIndex) => (
                              <Box
                                key={colorIndex}
                                w={{ base: 20, sm: 30 }}
                                h={{ base: 20, sm: 30 }}
                                style={{
                                  width: 30,
                                  height: 30,
                                  backgroundColor: color,
                                  borderRadius: 4,
                                  cursor: "pointer",
                                  border: "1px solid #ddd",
                                }}
                                onClick={() => handleColorClick(seasonalPhotos.indexOf(item), color)}
                              />
                            ))}
                            <ActionIcon
                              variant="subtle"
                              color="gray"
                              onClick={() => handleReset(seasonalPhotos.indexOf(item))}
                              disabled={JSON.stringify(item.colors) === JSON.stringify(item.originalColors)}
                              size={30}
                            >
                              <IconRefresh style={{ width: "70%", height: "70%" }} />
                            </ActionIcon>
                          </Flex>
                        </Paper>
                      ))}
                  </SimpleGrid>
                </Stack>
              ))}

              <Stack gap="md">
                <Divider
                  label={
                    <Text c="dimmed" fz="lg">
                      Undertone Check
                    </Text>
                  }
                />
                <Text size="sm" c="dimmed" ta="center" maw={560} mx="auto">
                  Bonus drapes to pin down your undertone. Compare each pair — gold vs silver, cream vs pure
                  white, tomato red vs cherry red. If gold, cream and tomato flatter you more, you're
                  warm-toned; if silver, white and cherry win, you're cool. If both look equally good, you're
                  likely neutral.
                </Text>
                <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="md">
                  {seasonalPhotos
                    .filter((photo) => isUndertonePhoto(photo.name))
                    .map((item, index) => (
                      <Paper key={index} withBorder p="xs" radius="md">
                        <MantineImage src={item.image} alt={`You in ${item.name} colors`} radius="md" />
                        <Flex gap="0" justify="space-evenly" align="center" wrap="nowrap" pt="xs">
                          {[...item.originalColors].reverse().map((color, colorIndex) => (
                            <Box
                              key={colorIndex}
                              w={{ base: 20, sm: 30 }}
                              h={{ base: 20, sm: 30 }}
                              style={{
                                width: 30,
                                height: 30,
                                backgroundColor: color,
                                borderRadius: 4,
                                cursor: "pointer",
                                border: "1px solid #ddd",
                              }}
                              onClick={() => handleColorClick(seasonalPhotos.indexOf(item), color)}
                            />
                          ))}
                          <ActionIcon
                            variant="subtle"
                            color="gray"
                            onClick={() => handleReset(seasonalPhotos.indexOf(item))}
                            disabled={JSON.stringify(item.colors) === JSON.stringify(item.originalColors)}
                            size={30}
                          >
                            <IconRefresh style={{ width: "70%", height: "70%" }} />
                          </ActionIcon>
                        </Flex>
                      </Paper>
                    ))}
                </SimpleGrid>
                <Text size="sm" ta="center">
                  <Text
                    component={Link}
                    href="/undertone-quiz"
                    c="wumpus.7"
                    fw={600}
                    onClick={() => trackCtaClick("vd_undertone_quiz")}
                  >
                    Still unsure? Take the 2-minute undertone quiz
                  </Text>
                </Text>
              </Stack>
            </Stack>
          </Paper>

          {/* Two co-equal next steps: ask the community (free) OR get your exact season (AI) */}
          <Text ta="center" fw={600} fz="lg" mt="xs">
            Can't tell which season is <em>you</em>? Two ways to find out:
          </Text>
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
            {/* Free path - community verdict on Reddit */}
            <Paper
              p="xl"
              radius="lg"
              withBorder
              style={{ borderColor: "var(--mantine-color-orange-3)", borderWidth: 2 }}
              bg="orange.0"
            >
              <Stack gap="md" align="center" h="100%" justify="space-between">
                <Stack gap="xs" align="center">
                  <Badge color="orange" variant="light" size="lg">
                    Free · Community
                  </Badge>
                  <Title order={3} ta="center" fw={700}>
                    Ask r/ColorAnalysis
                  </Title>
                  <Text ta="center" c="dimmed" fz="sm" maw={340}>
                    Post your 12 draped photos and let 100k+ color-analysis nerds vote on which season
                    makes your skin glow. We'll prep the post for you.
                  </Text>
                </Stack>
                <Button
                  size="lg"
                  color="orange"
                  fullWidth
                  leftSection={<IconUsers size={20} />}
                  onClick={() => {
                    trackCtaClick("vd_share_reddit_open");
                    openRedditModal();
                  }}
                >
                  Get a Second Opinion
                </Button>
              </Stack>
            </Paper>

            {/* The same paid analysis offer is available alongside free community sharing in every locale. */}
            {(
              <Paper
                p="xl"
                radius="lg"
                style={{ background: "linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)" }}
              >
                <Stack gap="md" align="center" h="100%" justify="space-between">
                  <Stack gap="xs" align="center">
                    <Badge color="white" variant="filled" size="lg" c="violet.7">
                      One-time · AI
                    </Badge>
                    <Title order={3} ta="center" c="white" fw={700}>
                      Complete AI Color Analysis
                    </Title>
                    <Text ta="center" c="white" style={{ opacity: 0.9 }} fz="sm" maw={340}>
                      Get an AI recommendation for your season, a personal palette, and guidance for
                      clothes, makeup and hair. Your report is generated after payment.
                    </Text>
                    <Text ta="center" c="white" style={{ opacity: 0.9 }} fz="sm" fw={600}>
                      Includes AI draping. Your free comparisons and downloads stay free.
                    </Text>
                  </Stack>
                  <Button
                    size="lg"
                    color="white"
                    variant="white"
                    fullWidth
                    rightSection={<IconArrowRight size={20} />}
                    onClick={() => handleUpgradeClick("vd_ai_analysis_bottom_card")}
                    loading={checkoutLoading}
                    style={{ color: "#7c3aed", fontWeight: 700 }}
                  >
                    {`Get My AI Analysis${priceDisplay ? ` — ${priceDisplay}` : " — $24.99"}`}
                  </Button>
                </Stack>
              </Paper>
            )}
          </SimpleGrid>
        </Stack>
      )}

      {checkoutError && <Alert color="red" role="alert" mt="md">{checkoutError}</Alert>}

      {photoSrc && showUnlockBar && !unlockBarDismissed && (
        <>
          <Box h={{ base: checkoutError ? 160 : 120, sm: checkoutError ? 100 : 60 }} />
          <Box
            pos="fixed"
            left={0}
            right={0}
            bottom={0}
            style={{
              background: "linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)",
              boxShadow: "0 -4px 20px rgba(124, 58, 237, 0.3)",
              zIndex: 500,
              transform: unlockBarVisible ? "translateY(0)" : "translateY(100%)",
              transition: "transform 0.4s cubic-bezier(0.22, 1, 0.36, 1)",
            }}
          >
            <Container size="xl" py="sm">
              {checkoutError && <Text role="alert" size="xs" c="white" mb="xs">{checkoutError}</Text>}
              <Flex align="center" justify="center" direction={{ base: "column", sm: "row" }} gap="xs" pos="relative" pr={24}>
                <Text fw={600} c="white" fz="sm">
                  Want your personal color report?
                </Text>
                <Button
                  color="white"
                  variant="white"
                  size="sm"
                  rightSection={<IconArrowRight size={16} />}
                  style={{ color: "#7c3aed", fontWeight: 700, flexShrink: 0 }}
                  onClick={() => handleUpgradeClick("vd_unlock_season_sticky")}
                  loading={checkoutLoading}
                >
                  {`Get report — ${priceDisplay ?? "$24.99"}`}
                </Button>
                <ActionIcon
                  variant="transparent"
                  c="white"
                  aria-label="Dismiss"
                  top={0}
                  pos="absolute"
                  right={0}
                  onClick={() => {
                    setUnlockBarVisible(false);
                    setUnlockBarDismissed(true);
                  }}
                  style={{ opacity: 0.85 }}
                >
                  <IconX size={18} />
                </ActionIcon>
              </Flex>
            </Container>
          </Box>
        </>
      )}

      <Modal opened={opened} onClose={handleCloseCamera} title="Take a Selfie" centered size="md">
        <Stack>
          <Box ref={videoBoxRef} style={{ position: "relative", width: "100%", height: 400 }}>
            <video
              ref={videoRef}
              autoPlay
              playsInline
              style={{
                display: "none",
              }}
            />
            <canvas
              ref={grayDrapeCanvasRef}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                pointerEvents: "none",
                zIndex: 2,
                display: "block",
                transform: "scaleX(1)",
              }}
            />
          </Box>
          <Button onClick={takePhoto} leftSection={<IconCamera size={18} />} color="wumpus">
            Capture Photo
          </Button>
        </Stack>
      </Modal>

      <Modal opened={redditModalOpened} onClose={closeRedditModal} title="Get your season verdict from r/ColorAnalysis" centered size="lg">
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            r/ColorAnalysis has 100k+ members who love calling seasons. Post your 12 draped photos and
            they'll tell you which one is you{canNativeShare ? "." : " — it takes two steps."}
          </Text>
          {canNativeShare && (
            <>
              <Paper withBorder p="md" radius="md" bg="orange.0" style={{ borderColor: "var(--mantine-color-orange-3)" }}>
                <Stack gap="xs">
                  <Text fw={600}>Your post is ready to go</Text>
                  <Text size="sm" c="dimmed">
                    We'll prep a Reddit post with all 12 drapes attached — pick Reddit in your share
                    sheet, paste the title we copy for you, and hit Post.
                  </Text>
                </Stack>
              </Paper>
              <Button
                leftSection={<RedditMark size={20} />}
                color={REDDIT_ORANGE}
                size="lg"
                fullWidth
                onClick={handleNativeShare}
              >
                Get Reddit's Opinion
              </Button>
              <Divider label="or do it manually" />
            </>
          )}
          {!hasDownloadedAll ? (
            <>
              <Paper withBorder p="md" radius="md" bg="orange.0" style={{ borderColor: "var(--mantine-color-orange-3)" }}>
                <Stack gap="xs">
                  <Group gap="xs">
                    <IconDownload size={18} color="var(--mantine-color-orange-6)" />
                    <Text fw={600}>Step 1: Download your 12 photos</Text>
                  </Group>
                  <Text size="sm" c="dimmed">
                    They save as a single ZIP, numbered 01–18 so they stay in order (12 seasons + 6 undertone
                    test drapes). You'll drag them into Reddit's uploader in the next step.
                  </Text>
                </Stack>
              </Paper>
              <Button
                leftSection={<IconDownload size={18} />}
                onClick={() => {
                  trackCtaClick("vd_share_reddit_download");
                  handleDownloadAll();
                }}
                loading={isProcessing}
                disabled={seasonalPhotos.length === 0}
                color="orange"
                size="md"
              >
                Download All 12 Photos
              </Button>
              <Divider label="or" />
              <Button variant="subtle" color="gray" onClick={() => setHasDownloadedAll(true)} size="sm">
                I already downloaded my photos
              </Button>
            </>
          ) : (
            <>
              <Paper withBorder p="md" radius="md" bg="green.0" style={{ borderColor: "var(--mantine-color-green-4)" }}>
                <Stack gap="xs">
                  <Group gap="xs">
                    <Text fw={600} c="green.8">Photos downloaded!</Text>
                  </Group>
                  <Text size="sm" c="dimmed">
                    Now post to r/ColorAnalysis. The community is very active and will help identify your season.
                  </Text>
                </Stack>
              </Paper>

              <Paper withBorder p="md" radius="md" bg="gray.0">
                <Stack gap="xs">
                  <Text fw={600} size="sm">Tips for a great post:</Text>
                  <Text size="sm" c="dimmed">- Upload all 12 season photos</Text>
                  <Text size="sm" c="dimmed">- Mention if you wore makeup or not (NMIP = no makeup in photos)</Text>
                  <Text size="sm" c="dimmed">- Include your natural hair color and eye color</Text>
                  <Text size="sm" c="dimmed">- Ask which seasons make your skin look brightest/healthiest</Text>
                </Stack>
              </Paper>

              <Button
                component="a"
                href={`https://www.reddit.com/r/ColorAnalysis/submit?title=${encodeURIComponent(
                  REDDIT_POST_TITLE
                )}&selftext=${encodeURIComponent(REDDIT_POST_BODY)}`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackCtaClick("vd_share_reddit_post")}
                leftSection={<RedditMark size={20} />}
                color={REDDIT_ORANGE}
                size="lg"
                fullWidth
              >
                Post to r/ColorAnalysis
              </Button>
              <Text size="xs" c="dimmed" ta="center">
                Opens Reddit with a pre-filled post template
              </Text>
            </>
          )}
        </Stack>
      </Modal>

      <canvas ref={canvasRef} style={{ display: "none" }} />
      <canvas ref={photoCanvasRef} style={{ display: "none" }} />
      <ColorNextSteps source="draping" />
    </Stack>
  );
}
